import { useState, useContext, useEffect, useMemo } from "react";
import { RestaurantCapabilitiesSection } from "./RestaurantCapabilitiesSection";
import { captureError } from "src/monitoring/sentry";
import Placeholder from "react-bootstrap/Placeholder";
import { MuiTelInput } from "mui-tel-input";
import { useGeolocated } from "react-geolocated";
import {
  APIProvider,
  Map,
  Marker as MapMarker,
} from "@vis.gl/react-google-maps";
import {
  InputSelect,
  TextInput,
  MultiSelect,
  SnackBar,
  CustomLoadingButton,
  InfoCard,
  ServiceRow,
  TipsCard,
} from "src/design/index";
import {
  Box,
  Grid,
  Typography,
  Button,
  Modal,
} from "@mui/material";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import PeopleOutlineIcon from "@mui/icons-material/PeopleOutline";
import RoomServiceOutlinedIcon from "@mui/icons-material/RoomServiceOutlined";
import LinkIcon from "@mui/icons-material/Link";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import { ConfigurationContext } from "src/Utils/Context/ConfigurationContext";
import { UserContext } from "src/Utils/Context/UserContext";
import { isFeatureSectionEnabled } from "src/helpers/FeatureCapabilityConfig";
import { useTranslation } from "react-i18next";
import {
  PaymentMethod,
  RestaurantInfoDetails,
  RestaurantType,
} from "src/business/restaurant/restaurant";
import {
  useUpdateRestaurant,
  RestaurantUpdateRequest,
} from "src/data/restaurant/queries/restaurant";
import { Cities } from "src/Utils/Data";
import {
  colorBackground,
  colorSurface,
  colorBorder,
  colorActionPrimary,
  colorActionPrimaryHover,
  colorActionPrimaryText,
  colorTextPrimary,
  colorTextMuted,
  radiusButton,
  radiusCard,
  shadowCard,
} from "src/design/tokens";

/** A booking always seats at least one guest. */
const MIN_PARTY_SIZE = 1;

interface Props {
  /**
   * The page's own read of the restaurant. Every field this form edits comes
   * from here rather than from the list row, so saving invalidates one query
   * this page owns instead of the list every screen shares.
   */
  restaurantDetails: RestaurantInfoDetails;
  /** The list row, for the two things the read model does not carry: id and capabilities. */
  restaurant: RestaurantType;
  admin: boolean;
}

function RestauInfo({ restaurantDetails, admin, restaurant }: Props) {
  const { configuration } = useContext(ConfigurationContext);
  const { user } = useContext(UserContext);
  const { t } = useTranslation();

  const canEditInfo = isFeatureSectionEnabled(
    ["restaurant_information_update"],
    "restaurant_management",
    user,
    restaurant.capabilities.map((c) => c.name),
  );

  const [isEditing, setIsEditing] = useState(false);
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [newInfo, setNewInfo] = useState<RestaurantInfoDetails>(restaurantDetails);
  const [editedFields, setEditedFields] = useState<Set<keyof typeof newInfo>>(
    new Set()
  );

  // Re-seed the form whenever a fresh read lands — a save invalidates the query,
  // so this is what puts the saved values on screen. Skipped while editing so a
  // background refetch cannot overwrite what the user is typing.
  useEffect(() => {
    if (isEditing) return;
    setNewInfo(restaurantDetails);
  }, [restaurantDetails, isEditing]);

  // Location modal state
  const [marker, setMarker] = useState(
    restaurantDetails.latitude != null
      ? { lat: restaurantDetails.latitude, lng: restaurantDetails.longitude }
      : null
  );
  const [canSaveLocation, setCanSaveLocation] = useState(false);

  const {
    updateRestaurant,
    updating,
    updateSuccess,
    updateError,
    resetUpdateStatus,
  } = useUpdateRestaurant(restaurant.id);

  const {
    updateRestaurant: updateLocation,
    updating: updatingLocation,
    updateSuccess: locationSuccess,
    updateError: locationError,
    resetUpdateStatus: resetLocationStatus,
  } = useUpdateRestaurant(restaurant.id);

  const { isGeolocationEnabled, coords, isGeolocationAvailable } =
    useGeolocated({
      positionOptions: { enableHighAccuracy: true },
      userDecisionTimeout: 5000,
    });

  const cityInfo = useMemo(
    () => Cities.find((c) => c.city === newInfo.city) ?? null,
    [newInfo.city]
  );

  const handleChange = (text: any, type: keyof typeof newInfo) => {
    setNewInfo((old) => ({ ...old, [type]: text }));
    setEditedFields((prev) => new Set(prev).add(type));
  };

  // The party cap can never exceed the seating the restaurant just declared in
  // the field above, so the live value of that field is the upper bound here.
  const seatCount = Number(newInfo.numberOfSeats) || null;

  const handleChangeMaximumPartySize = (value: string) => {
    const parsed = Number(value);
    if (value === "" || Number.isNaN(parsed)) {
      handleChange(null, "maximumPartySize");
      return;
    }
    // `max` on a number input is advisory — a typed or pasted value ignores it.
    const bounded = seatCount === null ? parsed : Math.min(seatCount, parsed);
    handleChange(Math.max(MIN_PARTY_SIZE, Math.round(bounded)), "maximumPartySize");
  };

  const handleSave = () => {
    const updateData: RestaurantUpdateRequest = {};

    const fieldMap: Partial<Record<keyof typeof newInfo, keyof RestaurantUpdateRequest>> = {
      name: "name",
      city: "cityName",
      address: "address",
      quartier: "quartier",
      phone: "phone",
      paymentMethods: "paymentMethods",
      numberOfSeats: "numberOfSeats",
      servesAlcohol: "servesAlcohol",
      hasAnimation: "hasAnimation",
      instaTag: "instaTag",
      maximumPartySize: "maximumPartySize",
    };

    editedFields.forEach((field) => {
      const value = newInfo[field];
      if (value === undefined || value === null || value === "") return;

      if (field === "tags") {
        updateData.tagIds = value as string[];
        return;
      }

      const requestKey = fieldMap[field];
      if (requestKey) {
        (updateData as any)[requestKey] = value;
      }
    });

    // Lowering the seat count after setting a cap leaves the two out of step;
    // the seat count wins, so the pair we send is always coherent.
    if (updateData.maximumPartySize !== undefined && seatCount !== null) {
      updateData.maximumPartySize = Math.min(
        updateData.maximumPartySize,
        seatCount
      );
    }

    updateRestaurant(updateData)
      .then(() => {
        setEditedFields(new Set());
        // Leaving edit mode lets the re-seed effect run, which lands the saved
        // values as soon as the invalidated read comes back. The mutation
        // invalidates the list row too, so the sidebar and dashboard follow.
        setIsEditing(false);
      })
      .catch((e) => captureError(e));
  };

  const handleCancel = () => {
    setNewInfo(restaurantDetails);
    setEditedFields(new Set());
    setIsEditing(false);
  };

  const handleSaveLocation = () => {
    if (!marker) return;
    updateLocation({
      coordinates: { latitude: marker.lat, longitude: marker.lng },
    })
      .then(() => {
        setCanSaveLocation(false);
        setLocationModalOpen(false);
      })
      .catch((e) => captureError(e));
  };

  const handleUseMyPosition = () => {
    if (!isGeolocationAvailable) {
      alert(t("restaurant.restaurantInfoPage.location.notSupported"));
      return;
    }
    if (!isGeolocationEnabled) {
      alert(t("restaurant.restaurantInfoPage.location.permissions"));
      return;
    }
    if (coords?.latitude && coords?.longitude) {
      setMarker({ lat: coords.latitude, lng: coords.longitude });
      setCanSaveLocation(true);
    } else {
      alert(t("restaurant.restaurantInfoPage.location.positionUnavailable"));
    }
  };

  const restaurantTags = useMemo(
    () =>
      configuration.tags
        .filter((tag) => newInfo.tags.includes(tag.id))
        .map((tag) => tag.name),
    [newInfo.tags, configuration.tags]
  );

  const disabled = !isEditing;

  const googleMapsKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;

  const mapDefaultCenter = marker ??
    (cityInfo
      ? { lat: Number(cityInfo.lat), lng: Number(cityInfo.lng) }
      : { lat: 33.5731, lng: -7.5898 }); // fallback: Casablanca

  if (!newInfo.name) {
    return (
      <>
        <Placeholder animation="glow">
          <Placeholder xs={7} />
        </Placeholder>
        <Placeholder animation="glow">
          <Placeholder xs={6} /> <Placeholder xs={3} />
          <Placeholder xs={10} />
          <Placeholder xs={10} />
        </Placeholder>
      </>
    );
  }

  return (
    <Box sx={{ backgroundColor: colorBackground, minHeight: "100%" }}>
      {/* Page header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 700, color: colorTextPrimary, mb: 0.5 }}>
            {t("restaurant.restaurantInfoPage.restaurantInfo.header")}
          </Typography>
          <Typography variant="body2" sx={{ color: colorTextMuted }}>
            {t("restaurant.restaurantInfoPage.restaurantInfo.subheader")}
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1 }}>
          {isEditing ? (
            <>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancel}
                sx={{ textTransform: "none", borderRadius: radiusButton, fontWeight: 500 }}
              >
                {t("common.cancel")}
              </Button>
              <CustomLoadingButton
                loading={updating}
                onSubmit={handleSave}
                label={t("common.save")}
                disabled={editedFields.size === 0}
                size="small"
                fullwidth={false}
                color="success"
              />
            </>
          ) : canEditInfo ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => setIsEditing(true)}
              sx={{
                textTransform: "none",
                borderRadius: radiusButton,
                fontWeight: 600,
                backgroundColor: colorActionPrimary,
                color: colorActionPrimaryText,
                "&:hover": { backgroundColor: colorActionPrimaryHover },
                boxShadow: "none",
              }}
            >
              {t("restaurant.restaurantInfoPage.restaurantInfo.editCta")}
            </Button>
          ) : null}
        </Box>
      </Box>

      <Grid container spacing={2} direction="column">
        {/* Capabilities — admin only */}
        {admin && (
          <Grid item>
            <RestaurantCapabilitiesSection restaurant={restaurant} />
          </Grid>
        )}

        {/* Basic Information */}
        <Grid item>
          <InfoCard
            title={t("restaurant.restaurantInfoPage.restaurantInfo.sections.basic")}
            subtitle={t("restaurant.restaurantInfoPage.restaurantInfo.sections.basicSub")}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextInput
                  disabled={disabled || !admin}
                  value={newInfo.name}
                  onChange={(v) => handleChange(v, "name")}
                  label={t(
                    "restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.name"
                  )}
                  fullwidth
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <MuiTelInput
                  label={t("common.phone")}
                  fullWidth
                  required
                  disabled={disabled}
                  value={newInfo.phone}
                  onChange={(v) => handleChange(v, "phone")}
                  defaultCountry="MA"
                  size="small"
                />
              </Grid>
            </Grid>
          </InfoCard>
        </Grid>

        {/* Location */}
        <Grid item>
          <InfoCard
            title={t("restaurant.restaurantInfoPage.restaurantInfo.sections.location")}
            subtitle={t("restaurant.restaurantInfoPage.restaurantInfo.sections.locationSub")}
            icon={<LocationOnOutlinedIcon fontSize="small" />}
            action={
              googleMapsKey && admin ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MyLocationIcon fontSize="small" />}
                  onClick={() => setLocationModalOpen(true)}
                  sx={{
                    textTransform: "none",
                    borderRadius: radiusButton,
                    fontWeight: 500,
                    fontSize: "0.75rem",
                  }}
                >
                  {t("restaurant.restaurantInfoPage.restaurantInfo.sections.locationCta")}
                </Button>
              ) : null
            }
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextInput
                  disabled={disabled}
                  value={newInfo.address}
                  onChange={(v) => handleChange(v, "address")}
                  label={t(
                    "restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.address"
                  )}
                  fullwidth
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextInput
                  disabled={disabled}
                  value={newInfo.quartier}
                  onChange={(v) => handleChange(v, "quartier")}
                  label={t(
                    "restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.quarter"
                  )}
                  fullwidth
                  required
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <InputSelect
                  disabled={disabled || !admin}
                  required
                  label={t(
                    "restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.city"
                  )}
                  onChange={(e) => handleChange(e.target.value, "city")}
                  value={newInfo.city}
                  size="small"
                  options={Cities.map((c) => c.city)}
                />
              </Grid>
            </Grid>
          </InfoCard>
        </Grid>

        {/* Capacity & Tags */}
        <Grid item>
          <InfoCard
            title={t("restaurant.restaurantInfoPage.restaurantInfo.sections.capacity")}
            subtitle={t("restaurant.restaurantInfoPage.restaurantInfo.sections.capacitySub")}
            icon={<PeopleOutlineIcon fontSize="small" />}
          >
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} sm={6}>
                <TextInput
                  disabled={disabled}
                  value={newInfo.numberOfSeats.toString()}
                  onChange={(v) => handleChange(v, "numberOfSeats")}
                  label={t(
                    "restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.numberOfSeats"
                  )}
                  fullwidth
                  type="number"
                  size="small"
                  required
                />
                <Typography
                  variant="caption"
                  sx={{ color: colorTextMuted, mt: 0.5, display: "block" }}
                >
                  {t("restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.numberOfSeatsHint")}
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <TextInput
                    disabled={disabled}
                    value={newInfo.maximumPartySize?.toString() ?? ""}
                    onChange={handleChangeMaximumPartySize}
                    label={t(
                      "restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.maximumPartySize"
                    )}
                    placeholder={t(
                      "restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.maximumPartySizePlaceholder"
                    )}
                    fullwidth
                    type="number"
                    size="small"
                    min={MIN_PARTY_SIZE}
                    max={seatCount ?? undefined}
                  />
                  <Typography
                    variant="caption"
                    sx={{ color: colorTextMuted, mt: 0.5, display: "block" }}
                  >
                    {t("restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.maximumPartySizeHint")}
                  </Typography>
                </Box>
              </Grid>
              {admin && (
                <Grid item xs={12} sm={6}>
                  <MultiSelect
                    disabled={disabled}
                    size="small"
                    value={restaurantTags}
                    label={t("common.tags")}
                    onChange={(names) => {
                      const tagIds = configuration.tags
                        .filter((tag) => names.includes(tag.name))
                        .map((tag) => tag.id);
                      handleChange(tagIds, "tags");
                    }}
                    options={configuration.tags}
                  />
                </Grid>
              )}
            </Grid>
          </InfoCard>
        </Grid>

        {/* Services & Amenities */}
        <Grid item>
          <InfoCard
            title={t("restaurant.restaurantInfoPage.restaurantInfo.sections.services")}
            subtitle={t("restaurant.restaurantInfoPage.restaurantInfo.sections.servicesSub")}
            icon={<RoomServiceOutlinedIcon fontSize="small" />}
          >
            <ServiceRow
              label={t(
                "restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.tpe"
              )}
              description={t("restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.tpeDesc")}
              checked={newInfo.paymentMethods === PaymentMethod.Tpe}
              onChange={(checked) =>
                handleChange(
                  checked ? PaymentMethod.Tpe : PaymentMethod.Cash,
                  "paymentMethods"
                )
              }
              disabled={disabled}
            />
            <ServiceRow
              label={t(
                "restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.booze"
              )}
              description={t("restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.boozeDesc")}
              checked={newInfo.servesAlcohol}
              onChange={(v) => handleChange(v, "servesAlcohol")}
              disabled={disabled}
            />
            <ServiceRow
              label={t(
                "restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.animation"
              )}
              description={t("restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.animationDesc")}
              checked={newInfo.hasAnimation}
              onChange={(v) => handleChange(v, "hasAnimation")}
              divider={false}
              disabled={disabled}
            />
          </InfoCard>
        </Grid>

        {/* Social Media & Links */}
        <Grid item>
          <InfoCard
            title={t("restaurant.restaurantInfoPage.restaurantInfo.sections.social")}
            subtitle={t("restaurant.restaurantInfoPage.restaurantInfo.sections.socialSub")}
            icon={<LinkIcon fontSize="small" />}
          >
            <TextInput
              disabled={disabled}
              value={newInfo.instaTag ?? ""}
              onChange={(v) => handleChange(v, "instaTag")}
              label={t(
                "restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.fields.instagram_link"
              )}
              fullwidth
              size="small"
            />
          </InfoCard>
        </Grid>

        <Grid item>
          <TipsCard
            title={t("restaurant.restaurantInfoPage.restaurantInfo.tips.title")}
            tips={[
              t("restaurant.restaurantInfoPage.restaurantInfo.tips.completeProfile"),
              t("restaurant.restaurantInfoPage.restaurantInfo.tips.socialLinks"),
              t("restaurant.restaurantInfoPage.restaurantInfo.tips.capacity"),
              t("restaurant.restaurantInfoPage.restaurantInfo.tips.tags"),
            ]}
          />
        </Grid>
      </Grid>

      {/* Location Modal */}
      {googleMapsKey && (
        <APIProvider apiKey={googleMapsKey}>
          <Modal
            open={locationModalOpen}
            onClose={() => setLocationModalOpen(false)}
          >
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
                width: { xs: "95vw", sm: "70vw", md: "55vw" },
                maxWidth: 1450,
                backgroundColor: colorSurface,
                borderRadius: radiusCard,
                boxShadow: shadowCard,
                overflow: "hidden",
              }}
            >
              {/* Modal header */}
              <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${colorBorder}` }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: colorTextPrimary }}>
                  {t("restaurant.restaurantInfoPage.location.header")}
                </Typography>
                <Typography variant="body2" sx={{ color: colorTextMuted }}>
                  {t("restaurant.restaurantInfoPage.location.subheader")}
                </Typography>
              </Box>

              {/* Map */}
              <Box sx={{ height: "50vh", width: "100%" }}>
                <Map
                  defaultCenter={mapDefaultCenter}
                  defaultZoom={15}
                  onClick={(e) => {
                    const latLng = e.detail.latLng;
                    if (!latLng?.lat || !latLng.lng) return;
                    setMarker({ lat: latLng.lat, lng: latLng.lng });
                    setCanSaveLocation(true);
                  }}
                >
                  {marker && <MapMarker position={marker} />}
                </Map>
              </Box>

              {/* Modal footer */}
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  borderTop: `1px solid ${colorBorder}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<MyLocationIcon fontSize="small" />}
                  onClick={handleUseMyPosition}
                  sx={{ textTransform: "none", borderRadius: radiusButton, fontWeight: 500 }}
                >
                  {t("restaurant.restaurantInfoPage.location.myPosition")}
                </Button>
                <Box sx={{ display: "flex", gap: 1 }}>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => setLocationModalOpen(false)}
                    sx={{ textTransform: "none", borderRadius: radiusButton }}
                  >
                    {t("common.cancel")}
                  </Button>
                  <CustomLoadingButton
                    loading={updatingLocation}
                    onSubmit={handleSaveLocation}
                    label={t("common.save")}
                    disabled={!canSaveLocation}
                    size="small"
                    fullwidth={false}
                    color="success"
                  />
                </Box>
              </Box>
            </Box>
          </Modal>
        </APIProvider>
      )}

      <SnackBar
        onClose={resetUpdateStatus}
        open={updateSuccess}
        message={t(
          "restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.uploadSuccess"
        )}
        severity="success"
      />
      <SnackBar
        onClose={resetUpdateStatus}
        open={updateError}
        message={t(
          "restaurant.restaurantInfoPage.restaurantInfo.ImportantInformation.uploadError"
        )}
        severity="error"
      />
      <SnackBar
        onClose={resetLocationStatus}
        open={locationSuccess}
        message={t("restaurant.restaurantInfoPage.location.uploadSuccess")}
        severity="success"
      />
      <SnackBar
        onClose={resetLocationStatus}
        open={locationError}
        message={t("restaurant.restaurantInfoPage.location.uploadError")}
        severity="error"
      />
    </Box>
  );
}

export default RestauInfo;
